import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Sundbyberg?", a: "Ansökan görs till tillståndsenheten i Sundbybergs stad. Verksamhet i annan kommun (t.ex. Solna, Stockholm, Danderyd, Järfälla) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Sundbyberg?", a: "Sundbybergs stad tar ut en ansökningsavgift och en årlig tillsynsavgift baserad på tillståndstyp och alkoholomsättning – aktuella avgifter finns på stadens webbplats." },
  { q: "Hur lång är handläggningstiden i Sundbyberg?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd. Tillfälliga tillstånd kan gå snabbare." },
];

export const Route = createFileRoute("/serveringstillstand-sundbyberg")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Sundbyberg" },
      ]}
      title="Serveringstillstånd Sundbyberg – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Sundbyberg: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.sundbyberg, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Sundbyberg?", body: "Tillståndsenheten i Sundbybergs stad ansvarar för serveringstillstånd i Sundbybergs kommun. För verksamhet i angränsande kommuner som Solna, Stockholm, Järfälla eller Danderyd ansöker du hos respektive kommun. Kraven enligt alkohollagen är nationella men blanketter, e-tjänster och lokala riktlinjer skiljer sig åt." },
        { h2: "Krav från Sundbybergs stad", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Sundbyberg en tät stadsbebyggelse med bostäder nära krogliv – lokala riktlinjer om serveringstider, ljud och uteservering är därför särskilt viktiga. Från 1 juni 2026 är de nationella kraven på eget kök och varierat matutbud borttagna, men kommunala ordningsvillkor kvarstår." },
        { h2: "Kunskapsprovet i Sundbyberg", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Det är ett skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning och nykterhet, marknadsföring m.m. Frågeantalet varierar beroende på tillståndstyp och kommun (FoHMFS 2014:7). Med Alkoholprovet tränar du i samma format hemifrån." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Ofullständig ansökan eller komplicerad lämplighetsprövning förlänger tiden. Lämna in i god tid innan planerad öppning." },
        { h2: "Tillsyn och uppföljning", body: "Sundbybergs stad bedriver aktiv tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Sveriges tätaste kommun", body: "Sundbyberg är Sveriges till ytan minsta och tätast befolkade kommun. Närheten till bostäder gör att buller från uteserveringar och nattöppna serveringar prövas hårt. Planera lokalen och serveringstiderna med detta i åtanke redan i ansökan." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-stockholm", label: "Serveringstillstånd Stockholm" },
          { to: "/serveringstillstand-solna", label: "Serveringstillstånd Solna" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-bar", label: "För bar & nattklubb" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Sundbyberg – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Sundbyberg: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Sundbyberg – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Sundbyberg." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-sundbyberg" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-sundbyberg" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Sundbyberg", url: "https://alkoholprovet.se/serveringstillstand-sundbyberg" },
      ]),
    ],
  }),
});
